/**
 * Door landing-page content — structure only; every sentence is an i18n key
 * in src/locales/english.json under `doors.<id>`.
 *
 * `renderDoor(id, t)` resolves a door to plain strings through the supplied
 * translate function (vue-i18n's `t` in the app, a JSON lookup in tests), so
 * the vocabulary rule for doors A and B ("Kubernetes", "vCluster", "namespace",
 * "ingress", "pod" never appear) can be asserted over exactly what renders.
 *
 * Design: documents/WEBSITE_REBUILD_PERSONA_DOORS_AND_CONTENT_MATRIX.md §6.
 */
import { DOORS, getDoor } from '../utils/persona.js'

export const FORBIDDEN_ON_NON_TECHNICAL_DOORS = Object.freeze([
  'kubernetes', 'vcluster', 'namespace', 'ingress', 'pod', 'kubectl'
])

/**
 * Per-door structure. Keys name the i18n leaves under `doors.<id>`; `to`
 * is an in-app route, `href` an external/full-load URL, `hash` an anchor on
 * the door page itself. `blocks` lists the door-specific sections (the
 * matrix's "New" row) in render order.
 */
const STRUCTURE = Object.freeze({
  owners: {
    hero: { secondaryTo: '/pricing' },
    steps: ['1', '2', '3'],
    proof: [
      { key: 'marketplace', icon: 'fas fa-store', to: '/marketplace' },
      { key: 'demo', icon: 'fas fa-play-circle', to: '/#demo' },
      { key: 'certificates', icon: 'fas fa-lock' },
      { key: 'backups', icon: 'fas fa-rotate-left' },
      { key: 'price', icon: 'fas fa-tag', to: '/pricing' }
    ],
    blocks: ['faq'],
    faq: ['1', '2', '3', '4']
  },
  agencies: {
    hero: { secondaryHash: '#handover' },
    steps: ['1', '2', '3'],
    proof: [
      { key: 'organisations', icon: 'fas fa-building' },
      { key: 'members', icon: 'fas fa-users' },
      { key: 'invitations', icon: 'fas fa-envelope-open-text' },
      { key: 'git', icon: 'fab fa-gitlab' },
      { key: 'domains', icon: 'fas fa-globe' }
    ],
    blocks: ['handover', 'cost'],
    handover: ['1', '2', '3'],
    costClients: [2, 4, 6],
    costPlanKey: 'team'
  },
  developers: {
    hero: { secondaryTo: '/#demo' },
    steps: ['1', '2', '3'],
    proof: [
      { key: 'pipeline', icon: 'fas fa-code-branch', href: '/docs' },
      { key: 'devEnv', icon: 'fas fa-laptop-code' },
      { key: 'remediation', icon: 'fas fa-wand-magic-sparkles', to: '/#demo' },
      { key: 'apiDocs', icon: 'fas fa-book', href: '/api/swagger/ui' },
      { key: 'logs', icon: 'fas fa-chart-line' }
    ],
    blocks: ['tutorial', 'share'],
    tutorial: ['1', '2', '3', '4', '5'],
    tutorialHref: '/docs',
    shareTo: '/for/engineering-leads'
  },
  'engineering-leads': {
    hero: { secondaryTo: '/contact?topic=walkthrough' },
    steps: ['1', '2', '3'],
    proof: [
      { key: 'architecture', icon: 'fas fa-diagram-project', to: '/platform' },
      { key: 'owasp', icon: 'fas fa-shield-halved', to: '/security' },
      { key: 'permissions', icon: 'fas fa-sitemap', to: '/security' },
      { key: 'monitoring', icon: 'fas fa-chart-area' },
      { key: 'backups', icon: 'fas fa-rotate-left' },
      { key: 'tls', icon: 'fas fa-certificate' },
      { key: 'standards', icon: 'fas fa-book-open', href: '/docs' }
    ],
    blocks: ['security', 'exit'],
    securityTo: '/security',
    enterpriseTo: '/for/self-managed'
  },
  'self-managed': {
    hero: { secondaryTo: '/security' },
    steps: ['1', '2', '3'],
    proof: [
      { key: 'custody', icon: 'fas fa-key', hash: '#custody' },
      { key: 'licence', icon: 'fas fa-file-signature' },
      { key: 'references', icon: 'fas fa-landmark' },
      { key: 'upgrades', icon: 'fas fa-arrow-up-right-dots' },
      { key: 'dr', icon: 'fas fa-server' },
      { key: 'support', icon: 'fas fa-headset' }
    ],
    blocks: ['buy', 'custody', 'support', 'checklist'],
    buy: ['1', '2', '3'],
    custodyRows: ['hardware', 'entitlement', 'keys', 'access', 'superadmin', 'buying'],
    support: ['1', '2', '3'],
    checklist: ['1', '2', '3', '4'],
    checklistTo: '/for/engineering-leads'
  }
})

export function doorStructure(id) {
  return STRUCTURE[id] || null
}

/**
 * Resolve a door's full content to strings.
 * @param {string} id door id
 * @param {(key: string, params?: object) => string} t translate function
 */
export function renderDoor(id, t) {
  const door = getDoor(id)
  const s = STRUCTURE[id]
  if (!door || !s) return null
  const k = (leaf) => `doors.${id}.${leaf}`

  const out = {
    id,
    slug: door.slug,
    technical: door.technical,
    label: t(door.labelKey),
    short: t(door.shortKey),
    cta: { label: t(door.cta.labelKey), to: door.cta.to, external: Boolean(door.cta.external), icon: door.cta.icon },
    hero: {
      title: t(k('hero.title')),
      lead: t(k('hero.lead')),
      secondary: t(k('hero.secondary')),
      secondaryTo: s.hero.secondaryTo || null,
      secondaryHash: s.hero.secondaryHash || null
    },
    pain: t(k('pain')),
    steps: s.steps.map((n) => ({ n, title: t(k(`steps.${n}.title`)), body: t(k(`steps.${n}.body`)) })),
    proof: s.proof.map((p) => ({
      key: p.key,
      icon: p.icon,
      to: p.to || null,
      href: p.href || null,
      hash: p.hash || null,
      title: t(k(`proof.${p.key}.title`)),
      body: t(k(`proof.${p.key}.body`))
    })),
    plans: door.plans,
    plansNote: t(k('plansNote')),
    blocks: s.blocks,
    footer: { title: t(k('footer.title')), body: t(k('footer.body')) }
  }

  if (s.faq) {
    out.faq = { title: t(k('faq.title')), items: s.faq.map((n) => ({ n, q: t(k(`faq.${n}.q`)), a: t(k(`faq.${n}.a`)) })) }
  }
  if (s.handover) {
    out.handover = {
      title: t(k('handover.title')),
      intro: t(k('handover.intro')),
      steps: s.handover.map((n) => t(k(`handover.${n}`))),
      note: t(k('handover.note'))
    }
  }
  if (s.costClients) {
    out.cost = {
      title: t(k('cost.title')),
      intro: t(k('cost.intro')),
      footnote: t(k('cost.footnote')),
      unavailable: t(k('cost.unavailable')),
      clients: s.costClients,
      planKey: s.costPlanKey
    }
  }
  if (s.tutorial) {
    out.tutorial = {
      title: t(k('tutorial.title')),
      intro: t(k('tutorial.intro')),
      steps: s.tutorial.map((n) => t(k(`tutorial.${n}`))),
      link: t(k('tutorial.link')),
      href: s.tutorialHref
    }
    out.share = { title: t(k('share.title')), body: t(k('share.body')), link: t(k('share.link')), to: s.shareTo }
  }
  if (s.securityTo) {
    out.security = { title: t(k('security.title')), body: t(k('security.body')), link: t(k('security.link')), to: s.securityTo }
    out.exit = { title: t(k('exit.title')), body: t(k('exit.body')) }
    out.enterprise = { text: t(k('enterpriseLink')), to: s.enterpriseTo }
  }
  if (s.custodyRows) {
    out.buy = { title: t(k('buy.title')), body: t(k('buy.body')), items: s.buy.map((n) => t(k(`buy.${n}`))) }
    out.custody = {
      title: t(k('custody.title')),
      intro: t(k('custody.intro')),
      colHosted: t(k('custody.colHosted')),
      colSelf: t(k('custody.colSelf')),
      rows: s.custodyRows.map((r) => ({
        key: r,
        label: t(k(`custody.rows.${r}.label`)),
        hosted: t(k(`custody.rows.${r}.hosted`)),
        self: t(k(`custody.rows.${r}.self`))
      }))
    }
    out.support = { title: t(k('support.title')), items: s.support.map((n) => t(k(`support.${n}`))) }
    out.checklist = {
      title: t(k('checklist.title')),
      intro: t(k('checklist.intro')),
      items: s.checklist.map((n) => t(k(`checklist.${n}`))),
      outro: t(k('checklist.outro')),
      link: t(k('checklist.link')),
      to: s.checklistTo
    }
    out.licenceBlock = { title: t(k('licenceBlock.title')), body: t(k('licenceBlock.body')) }
  }
  return out
}

/** Every string in a rendered door, flattened — used by the vocabulary test. */
export function collectStrings(value, acc = []) {
  if (value === null || value === undefined) return acc
  if (typeof value === 'string') { acc.push(value); return acc }
  if (Array.isArray(value)) { value.forEach((v) => collectStrings(v, acc)); return acc }
  if (typeof value === 'object') { Object.values(value).forEach((v) => collectStrings(v, acc)); return acc }
  return acc
}

// ── Homepage middle sections (phase 2) ──────────────────────────────────────

/**
 * Which demo video leads on the homepage, per door. Owners, agencies and the
 * neutral homepage lead with the five-minute deployment; developers with the
 * AI remediation (design doc §8: deployment is door A's proof, remediation
 * door C's).
 */
const HOME_VIDEO_ORDER = Object.freeze({
  browsing: ['deploy', 'remediation'],
  owners: ['deploy', 'remediation'],
  agencies: ['deploy', 'remediation'],
  developers: ['remediation', 'deploy'],
  'engineering-leads': ['deploy', 'remediation'],
  'self-managed': ['deploy', 'remediation']
})

export const HOME_FLOW_STEPS = Object.freeze(['1', '2', '3', '4'])

/** The homepage key for a door id; null / unknown → the neutral "browsing" copy. */
export function homeKey(doorId) {
  return getDoor(doorId) ? doorId : 'browsing'
}

/**
 * Resolve the homepage's door-driven copy ("in action" videos + the four-step
 * workflow) to strings. Non-technical doors (A, B) and browsing get the
 * plain video captions; technical doors get the technical ones.
 * @param {string|null} doorId
 * @param {(key: string, params?: object) => string} t
 */
export function renderHome(doorId, t) {
  const id = homeKey(doorId)
  const door = getDoor(id)
  const variant = door && door.technical ? 'technical' : 'plain'
  const k = (leaf) => `homeDoors.${id}.${leaf}`
  return {
    id,
    variant,
    demo: {
      title: t(k('demo.title')),
      lead: t(k('demo.lead')),
      videos: HOME_VIDEO_ORDER[id].map((key) => {
        const v = (leaf) => `homeDoors.videos.${key}.${variant}.${leaf}`
        return {
          key,
          title: t(v('title')),
          body: t(v('body')),
          points: ['1', '2', '3'].map((n) => t(v(`points.${n}`)))
        }
      })
    },
    flow: {
      title: t(k('flow.title')),
      lead: t(k('flow.lead')),
      steps: HOME_FLOW_STEPS.map((n) => ({ n, title: t(k(`flow.steps.${n}.title`)), body: t(k(`flow.steps.${n}.body`)) }))
    }
  }
}

export { DOORS }
